import { useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import { ButtonLoader, Loader } from '../../../components'
import { fetchCoverageEventDrift, addCoverageEventLocations } from '../../../services/coverageEventServices'
import { coverageEventQueryKeys } from './coverageEventQueryKeys'
import { mapCoverageEventError } from './coverageEventErrors'

const T = 'cycle_counts.events.drift'

// gateOff hides only the write action ("Añadir al evento"); the drift list itself stays
// readable gate-off, matching the platform.
export const CoverageEventDrift = ({ eventId, enabled, gateOff, onAdded, onGateOff }) => {
  const { i18n } = useTranslation()
  const queryClient = useQueryClient()
  const [selected, setSelected] = useState([])
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState(null)
  const { data, isLoading, isError } = useQuery(coverageEventQueryKeys.drift(eventId), () => fetchCoverageEventDrift(eventId), {
    refetchOnWindowFocus: false,
    enabled,
  })
  const rows = data?.locations ?? []

  const add = async () => {
    setBusy(true)
    setMessage(null)
    try {
      const result = await addCoverageEventLocations(eventId, selected)
      setMessage({ ok: true, text: i18n.t(`${T}.added`, result) })
      setSelected([])
      queryClient.invalidateQueries(coverageEventQueryKeys.drift(eventId))
      queryClient.invalidateQueries(coverageEventQueryKeys.detailAny(eventId))
      onAdded?.()
    } catch (err) {
      const mapped = mapCoverageEventError(err, i18n)
      if (mapped.gateOff) onGateOff?.()
      if (mapped.refetch) queryClient.invalidateQueries(coverageEventQueryKeys.detailAny(eventId))
      setMessage({ ok: false, text: mapped.message })
    } finally {
      setBusy(false)
    }
  }

  if (!enabled) return <p className="text-sm text-gray-500">{i18n.t(`${T}.not_open`)}</p>
  if (isLoading) return <Loader show />
  if (isError) return <p className="text-sm text-red-700">{i18n.t('cycle_counts.events.errors.UNEXPECTED')}</p>
  if (rows.length === 0) {
    if (message) return <p className={`text-sm ${message.ok ? 'text-green-700' : 'text-red-700'}`}>{message.text}</p>
    return <p className="text-sm text-gray-500">{i18n.t(`${T}.empty`)}</p>
  }

  return (
    <div className="space-y-3">
      <ul className="space-y-1">
        {rows.map(row => (
          <li key={row.warehouse_location_id} className="text-sm">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                aria-label={row.location_code}
                checked={selected.includes(row.warehouse_location_id)}
                onChange={e =>
                  setSelected(e.target.checked ? [...selected, row.warehouse_location_id] : selected.filter(id => id !== row.warehouse_location_id))
                }
              />
              <span className="font-mono">{row.location_code}</span>
              {row.has_active_count && <span className="text-xs text-gray-500">({i18n.t(`${T}.has_active_count`)})</span>}
            </label>
          </li>
        ))}
      </ul>
      {message && <p className={`text-sm ${message.ok ? 'text-green-700' : 'text-red-700'}`}>{message.text}</p>}
      {!gateOff && (
        <ButtonLoader type="primary" onClick={add} loading={busy} disabled={busy || selected.length === 0}>
          {i18n.t(`${T}.add_selected`)}
        </ButtonLoader>
      )}
    </div>
  )
}
